import { useDeferredValue, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Barcode, Printer, Trash2 } from 'lucide-react';
import {
  ApiErrorCode,
  LABEL_MAX_PER_JOB,
  LABEL_MAX_PER_VARIANT,
  LABEL_MAX_VARIANTS,
  PERMISSIONS,
  ProductStatus,
  type ProductSummary,
  type ProductVariantSummary,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { useLocale } from '@/hooks/useLocale';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorCode, apiErrorDetails, apiErrorMessage } from '@/services/apiClient';
import {
  productQueryOptions,
  useGenerateBarcodesMutation,
  useLabelSheetQuery,
} from '../api/catalog.api';
import { ProductSearch } from '../components/ProductSearch';
import {
  LABEL_QUANTITY_PRESETS,
  jobProblem,
  labelLine,
  labelRequest,
  mergeLines,
  parseQuantity,
  totalLabels,
  type LabelBuilderState,
  type LabelLine,
  type LabelPrintState,
} from '../labels/labelLines';
import {
  LABEL_PRESETS,
  LABEL_PRESET_ORDER,
  labelsPerPage,
  type LabelPresetId,
} from '../labels/labelPresets';
import { LabelSheetView } from '../labels/LabelSheetView';

/** Pages a preview lays out; printing always lays out every one. */
const PREVIEW_PAGES = 2;
/** Millimetres to CSS pixels at the browser's 96 dpi. */
const PX_PER_MM = 96 / 25.4;

function isBuilderState(value: unknown): value is LabelBuilderState {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as { lines?: unknown }).lines)
  );
}

/**
 * Barcode labels for any set of variants: what goes on them, how many of each, on which stock.
 *
 * Labels identify a variant, never a single piece. The preview is the server's own label content,
 * laid out exactly as it prints; printing opens a page outside the application shell. A variant with
 * no barcode is named, and only an explicit action by someone who may edit the catalogue issues one.
 */
export function LabelBuilderPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const formId = useId();

  const [entry] = useState(() => (isBuilderState(location.state) ? location.state : null));
  const [lines, setLines] = useState<LabelLine[]>(() => entry?.lines ?? []);
  const [presetId, setPresetId] = useState<LabelPresetId>(
    () => entry?.options?.presetId ?? 'THERMAL_50x25',
  );
  const [showPrice, setShowPrice] = useState(() => entry?.options?.showPrice ?? true);
  const [showSku, setShowSku] = useState(() => entry?.options?.showSku ?? false);
  const [addError, setAddError] = useState<string | null>(null);
  const [generated, setGenerated] = useState<string | null>(null);

  const preset = LABEL_PRESETS[presetId];
  const problem = jobProblem(lines);
  const total = totalLabels(lines);
  const request = useDeferredValue(labelRequest(lines, { showPrice, showSku }));
  const sheetQuery = useLabelSheetQuery(request);
  const generate = useGenerateBarcodesMutation();
  const canEdit = can(PERMISSIONS.CATALOG_EDIT);

  const addVariants = (variants: ProductVariantSummary[], product: ProductSummary) => {
    setAddError(null);
    setGenerated(null);
    setLines((current) =>
      mergeLines(
        current,
        variants
          .filter((variant) => variant.isActive)
          .map((variant) => labelLine(variant, product)),
      ),
    );
  };

  const addProduct = (product: ProductSummary) => {
    setAddError(null);
    queryClient
      .fetchQuery(productQueryOptions(product.id))
      .then((detail) => {
        const active = detail.variants.filter((variant) => variant.isActive);
        if (detail.status !== ProductStatus.ACTIVE || active.length === 0) {
          setAddError(t('catalog.labels.noActiveVariants', { name: detail.name }));
          return;
        }
        addVariants(active, detail);
      })
      .catch((caught: unknown) =>
        setAddError(apiErrorMessage(caught) ?? t('catalog.labels.addError')),
      );
  };

  const setQuantity = (variantId: string, quantity: string) =>
    setLines((current) =>
      current.map((line) => (line.variantId === variantId ? { ...line, quantity } : line)),
    );

  const removeLine = (variantId: string) =>
    setLines((current) => current.filter((line) => line.variantId !== variantId));

  // The server names each unlabelled variant by SKU; the builder maps them back to its own lines.
  const missingSkus =
    apiErrorCode(sheetQuery.error) === ApiErrorCode.VARIANT_HAS_NO_BARCODE
      ? apiErrorDetails(sheetQuery.error).map((detail) => detail.message)
      : [];
  const missingLines = lines.filter((line) => missingSkus.includes(line.sku));

  const generateMissing = () => {
    setGenerated(null);
    generate.mutate(
      missingLines.map((line) => line.variantId),
      {
        onSuccess: (result) =>
          setGenerated(t('catalog.labels.generated', { count: result.generated.length })),
      },
    );
  };

  const print = () => {
    if (!request || !sheetQuery.data) return;
    const state: LabelPrintState = {
      request,
      presetId,
      builder: { lines, options: { presetId, showPrice, showSku } },
    };
    void navigate('/print/labels', { state });
  };

  const problemText =
    problem === 'QUANTITY'
      ? t('catalog.labels.quantityInvalid', { max: LABEL_MAX_PER_VARIANT })
      : problem === 'TOO_MANY_LABELS'
        ? t('catalog.labels.tooManyLabels', { max: LABEL_MAX_PER_JOB })
        : problem === 'TOO_MANY_VARIANTS'
          ? t('catalog.labels.tooManyVariants', { max: LABEL_MAX_VARIANTS })
          : null;

  const pages = Math.ceil(total / labelsPerPage(preset));

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('catalog.labels.title')}
        subtitle={t('catalog.labels.subtitle')}
        actions={
          <Button
            onClick={print}
            disabled={problem !== null || !sheetQuery.data || sheetQuery.isFetching}
          >
            <Printer className="size-4" aria-hidden />
            {t('catalog.labels.print')}
          </Button>
        }
      />

      <div className="grid grid-cols-[minmax(0,1fr)] gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="space-y-6">
          <Card>
            <CardHeader title={t('catalog.labels.add')} subtitle={t('catalog.labels.addHint')} />
            <div className="space-y-3 p-5">
              <ProductSearch
                onSelectProduct={addProduct}
                onSelectVariant={(variant, product) => addVariants([variant], product)}
                maxResults={5}
              />
              {addError ? <Alert tone="warning">{addError}</Alert> : null}
            </div>
          </Card>

          <Card>
            <CardHeader
              title={t('catalog.labels.lines')}
              subtitle={t('catalog.labels.total', { count: total })}
            />
            {lines.length === 0 ? (
              <EmptyState
                icon={Barcode}
                title={t('catalog.labels.empty')}
                description={t('catalog.labels.emptyHint')}
                className="px-4 py-8"
              />
            ) : (
              <ul className="divide-line divide-y" aria-label={t('catalog.labels.lines')}>
                {lines.map((line) => (
                  <LineRow
                    key={line.variantId}
                    line={line}
                    locale={locale}
                    missing={missingSkus.includes(line.sku)}
                    onQuantity={(quantity) => setQuantity(line.variantId, quantity)}
                    onRemove={() => removeLine(line.variantId)}
                  />
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader title={t('catalog.labels.options')} />
            <div className="grid gap-4 p-5 sm:grid-cols-2">
              <div>
                <label htmlFor={`${formId}-preset`} className="field-label">
                  {t('catalog.labels.layout')}
                </label>
                <Select
                  id={`${formId}-preset`}
                  value={presetId}
                  onChange={(event) => setPresetId(event.target.value as LabelPresetId)}
                  options={LABEL_PRESET_ORDER.map((id) => ({
                    value: id,
                    label: t(`catalog.labels.presets.${id}`),
                  }))}
                />
              </div>
              <fieldset className="space-y-2">
                <legend className="field-label">{t('catalog.labels.show')}</legend>
                <label className="text-ink flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={showPrice}
                    onChange={(event) => setShowPrice(event.target.checked)}
                  />
                  {t('catalog.labels.showPrice')}
                </label>
                <label className="text-ink flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={showSku}
                    onChange={(event) => setShowSku(event.target.checked)}
                  />
                  {t('catalog.labels.showSku')}
                </label>
              </fieldset>
            </div>
          </Card>
        </div>

        <Card>
          <CardHeader
            title={t('catalog.labels.preview')}
            subtitle={
              total > 0 && problem === null
                ? t('catalog.labels.pages', { count: pages })
                : undefined
            }
          />
          <div className="space-y-4 p-5">
            {problemText ? <Alert tone="error">{problemText}</Alert> : null}

            {missingLines.length > 0 ? (
              <Alert tone="warning">
                <p>{t('catalog.labels.noBarcode', { count: missingLines.length })}</p>
                <ul className="mt-1 font-mono text-xs" dir="ltr">
                  {missingLines.map((line) => (
                    <li key={line.variantId}>{line.sku}</li>
                  ))}
                </ul>
                <div className="mt-3 flex flex-wrap gap-2">
                  {canEdit ? (
                    <Button size="sm" onClick={generateMissing} isLoading={generate.isPending}>
                      {t('catalog.labels.generate', { count: missingLines.length })}
                    </Button>
                  ) : (
                    <span className="text-sm">{t('catalog.labels.askToGenerate')}</span>
                  )}
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      setLines((current) =>
                        current.filter((line) => !missingSkus.includes(line.sku)),
                      )
                    }
                  >
                    {t('catalog.labels.removeMissing')}
                  </Button>
                </div>
              </Alert>
            ) : null}
            {generate.isError ? (
              <Alert tone="error">
                {apiErrorMessage(generate.error) ?? t('catalog.labels.generateError')}
              </Alert>
            ) : null}
            {generated ? <Alert tone="success">{generated}</Alert> : null}

            {sheetQuery.isError && missingLines.length === 0 ? (
              <Alert tone="error">
                {apiErrorMessage(sheetQuery.error) ?? t('catalog.labels.previewError')}
              </Alert>
            ) : null}

            {problem === null && sheetQuery.data ? (
              <>
                <ScaledPreview pageWidthMm={preset.page.width}>
                  <LabelSheetView
                    sheet={sheetQuery.data}
                    preset={preset}
                    locale={locale}
                    maxPages={PREVIEW_PAGES}
                  />
                </ScaledPreview>
                {pages > PREVIEW_PAGES ? (
                  <p className="text-muted text-xs">
                    {t('catalog.labels.previewLimited', { count: PREVIEW_PAGES, total: pages })}
                  </p>
                ) : null}
              </>
            ) : problem === 'EMPTY' ? (
              <p className="text-muted text-sm">{t('catalog.labels.previewEmpty')}</p>
            ) : sheetQuery.isFetching ? (
              <p className="text-muted text-sm">{t('common.loading')}</p>
            ) : null}
          </div>
        </Card>
      </div>
    </div>
  );
}

function LineRow({
  line,
  locale,
  missing,
  onQuantity,
  onRemove,
}: {
  line: LabelLine;
  locale: string;
  missing: boolean;
  onQuantity: (quantity: string) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const name = locale === 'ar' ? (line.productNameAr ?? line.productName) : line.productName;
  const invalid = parseQuantity(line.quantity) === null;

  return (
    <li className="flex flex-wrap items-center gap-3 px-5 py-3" data-testid="label-line">
      <span className="min-w-0 flex-1 basis-40">
        <span className="text-ink block truncate text-sm font-medium" dir="auto">
          {name}
        </span>
        <span className="text-muted block text-xs">
          <span className="font-mono" dir="ltr">
            {line.sku}
          </span>
          {line.variantName ? (
            <span className="mx-1.5">
              · <bdi>{line.variantName}</bdi>
            </span>
          ) : null}
          {missing ? (
            <span className="ms-1.5 text-amber-700">· {t('catalog.labels.missingBarcode')}</span>
          ) : null}
        </span>
      </span>
      <span className="flex items-center gap-2">
        <span
          className="flex items-center gap-1"
          role="group"
          aria-label={t('catalog.labels.quickQuantity')}
        >
          {LABEL_QUANTITY_PRESETS.map((preset) => (
            <Button
              key={preset}
              size="sm"
              variant={line.quantity === String(preset) ? 'primary' : 'secondary'}
              onClick={() => onQuantity(String(preset))}
              aria-label={t('catalog.labels.setQuantity', { count: preset, sku: line.sku })}
            >
              {preset}
            </Button>
          ))}
        </span>
        <span className="w-24">
          <Input
            inputMode="numeric"
            dir="ltr"
            aria-label={t('catalog.labels.quantityFor', { sku: line.sku })}
            value={line.quantity}
            onChange={(event) => onQuantity(event.target.value)}
            error={
              invalid
                ? t('catalog.labels.quantityShort', { max: LABEL_MAX_PER_VARIANT })
                : undefined
            }
          />
        </span>
        <Button
          size="sm"
          variant="ghost"
          onClick={onRemove}
          aria-label={t('catalog.labels.removeLine', { sku: line.sku })}
        >
          <Trash2 className="size-4" aria-hidden />
        </Button>
      </span>
    </li>
  );
}

/** Shrinks the true-size pages to the card's width, so an A4 sheet fits a phone without being redrawn. */
function ScaledPreview({ pageWidthMm, children }: { pageWidthMm: number; children: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const element = box.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width ?? 0;
      if (width > 0) setScale(Math.min(1, width / (pageWidthMm * PX_PER_MM)));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [pageWidthMm]);

  return (
    <div ref={box} className="label-preview" data-testid="label-preview">
      <div className="label-preview__pages" style={{ zoom: scale }}>
        {children}
      </div>
    </div>
  );
}
